import { SlideObject, SlideObjectStyle } from '../types';

export function isLineShape(style?: SlideObjectStyle): boolean {
  return style?.shape === 'line';
}

export function isFillableShape(object: SlideObject | null | undefined): boolean {
  return Boolean(object && object.type === 'shape' && !isLineShape(object.style));
}

export function hasEditableText(object: SlideObject | null | undefined): boolean {
  return Boolean(object && (object.type === 'text' || isFillableShape(object)));
}

export type ObjectHoverCursor = 'text' | 'grab' | 'move' | 'default' | 'copy';

export function objectHoverCursor(
  object: Pick<SlideObject, 'type' | 'locks'>,
  options?: {
    editing?: boolean;
    cropping?: boolean;
    selectedCount?: number;
    isSelected?: boolean;
    moveModifier?: boolean;
    copyModifier?: boolean;
  },
): ObjectHoverCursor {
  if (options?.cropping) return 'grab';
  if (options?.editing) return 'text';
  if (options?.copyModifier && !object.locks?.delete) return 'copy';
  const multiMove = Boolean(options?.isSelected && (options.selectedCount || 0) > 1);
  if ((options?.moveModifier || multiMove) && !object.locks?.position) return 'move';
  if (object.type === 'text' || object.type === 'table') {
    if (!object.locks?.content) return 'text';
    return object.locks.position ? 'default' : 'move';
  }
  if (object.locks?.position) return 'default';
  return 'move';
}

export function hasShapeMedia(object: SlideObject | null | undefined): boolean {
  return Boolean(isFillableShape(object) && (object?.assetId || object?.imageUrl));
}

export function isCroppableObject(object: SlideObject | null | undefined): boolean {
  return Boolean(object && (object.type === 'image' || object.type === 'product' || hasShapeMedia(object)));
}

export const DEFAULT_TEXT_PADDING = 4;

export function resolvedTextPadding(object: SlideObject): number {
  if (object.style?.padding != null) return Number(object.style.padding);
  return object.type === 'text' ? DEFAULT_TEXT_PADDING : 0;
}

export function hexLuminance(color?: string): number {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(color || ''));
  if (!match) return 255;
  const value = parseInt(match[1], 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function isDarkFill(color?: string): boolean {
  if (!color || color === 'transparent') return false;
  return hexLuminance(color) < 140;
}

export function defaultShapeTextStyle(style?: SlideObjectStyle): Partial<SlideObjectStyle> {
  return {
    fontFamily: 'Kapra Neue',
    fontSize: 28,
    fontWeight: 600,
    color: isDarkFill(style?.backgroundColor) ? '#FFFFFF' : '#1B3967',
    textTransform: 'uppercase',
    textAlign: 'center',
    verticalAlign: 'middle',
    lineHeight: 1.05,
    padding: 16,
  };
}

export function withShapeTextDefaults(object: SlideObject): SlideObject {
  if (!isFillableShape(object) || object.style?.fontFamily) return object;
  return { ...object, style: { ...object.style, ...defaultShapeTextStyle(object.style) } };
}

export function shapeClipPathCss(width: number, height: number, style?: SlideObjectStyle): string {
  const kind = style?.shape || 'rect';
  if (kind === 'ellipse') return 'ellipse(50% 50% at 50% 50%)';
  if (kind === 'triangle') return 'polygon(50% 0%, 100% 100%, 0% 100%)';
  if (kind === 'roundRect' || ((kind === 'rect' || !kind) && (style?.cornerRadius || 0) > 0)) {
    const radius = style?.cornerRadius != null ? style.cornerRadius : Math.min(width, height) * 0.12;
    return `inset(0 round ${Math.max(0, radius)}px)`;
  }
  return 'none';
}
